import { Falling, PlayerTag, Transform } from '@/components'
import type { SystemFn } from '@/core/ecs/Scheduler'
import { Events } from '@/game/events'
import { EventsResource, SettingsResource } from '@/game/resources'
import type { Renderer } from '@/gfx/Renderer'

/**
 * Keeps both fighters framed and turns impacts into camera trauma.
 *
 * The camera never rotates: on a shared screen, a moving viewpoint costs one
 * player their spatial reference every time it swings. It only drifts and
 * breathes, and shakes when something hits hard.
 */
export function createCameraSystem(renderer: Renderer): SystemFn {
  return ({ world, dt }) => {
    const events = world.resource(EventsResource)
    const settings = world.resource(SettingsResource)

    // Bounding box over everyone still standing, so the framing works for a
    // duel and for a six-way brawl without special cases.
    let minX = Infinity
    let minZ = Infinity
    let maxX = -Infinity
    let maxZ = -Infinity
    let found = 0

    for (const e of world.query([PlayerTag, Transform]).entities()) {
      if (world.has(e, Falling)) continue
      const tr = world.read(e, Transform)
      minX = Math.min(minX, tr.x)
      maxX = Math.max(maxX, tr.x)
      minZ = Math.min(minZ, tr.z)
      maxZ = Math.max(maxZ, tr.z)
      found++
    }

    if (found > 0) {
      renderer.frameTargets(minX, minZ, maxX, maxZ, dt, settings.staticCamera)
    }

    for (const shake of events.read(Events.Shake)) {
      renderer.addShake(shake.strength, settings.screenShake)
    }
    for (const hit of events.read(Events.Hit)) {
      if (hit.kind === 'bump') continue
      // Normalised against 40 m/s — the top of the launch table — so the
      // channel stops saturating a third of the way up. Below that ceiling the
      // strongest hit in the game and a mediocre one shook the screen equally,
      // which is the same as not shaking at all. The renderer squares this.
      renderer.addShake(Math.min(1.2, hit.power / 40), settings.screenShake)
    }
    for (const fall of events.read(Events.Fall)) {
      if (fall.player !== null) renderer.addShake(0.5, settings.screenShake)
    }
  }
}
